import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Col, Drawer, Form, Input, InputNumber, Popconfirm,
  Row, Select, Space, Table, Tabs, Tag, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  Equipment, getEquipment, createEquipment, updateEquipment, deleteEquipment,
} from '../api/equipment';
import { EquipmentType, getEquipmentTypes } from '../api/equipment-types';
import { getWorkplaces, Workplace } from '../api/workplaces';
import api from '../api/client';
import MaintenanceLog from '../components/MaintenanceLog';

const { Title } = Typography;

interface Department { id: number; name: string; }

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  repair: 'orange',
  stored: 'blue',
  written_off: 'default',
};
const STATUS_LABELS: Record<string, string> = {
  active: 'В работе',
  repair: 'В ремонте',
  stored: 'На хранении',
  written_off: 'Списан',
};

const EquipmentPage: React.FC = () => {
  const [items, setItems] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState<EquipmentType[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [allWorkplaces, setAllWorkplaces] = useState<Workplace[]>([]);
  const [filteredWorkplaces, setFilteredWorkplaces] = useState<Workplace[]>([]);

  // Filters
  const [filterType, setFilterType] = useState<number | undefined>();
  const [filterDept, setFilterDept] = useState<number | undefined>();
  const [filterSearch, setFilterSearch] = useState('');

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<Equipment | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    getEquipmentTypes().then(setTypes);
    getWorkplaces().then(data => {
      setAllWorkplaces(data);
      setFilteredWorkplaces(data);
    });
    api.get<Department[]>('/departments').then(r => {
      const data = r.data;
      setDepartments(Array.isArray(data) ? data : (data as any).items ?? []);
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getEquipment({
        category: 'equipment',
        type_id: filterType,
        department_id: filterDept,
        search: filterSearch || undefined,
      });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterDept, filterSearch]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFormDeptChange = useCallback((deptId: number | undefined) => {
    if (deptId) {
      const validWorkplaces = allWorkplaces.filter(w => w.department_id === deptId);
      setFilteredWorkplaces(validWorkplaces);
      const validIds = new Set(validWorkplaces.map(w => w.id));
      const current: number[] = form.getFieldValue('workplace_ids') || [];
      form.setFieldValue('workplace_ids', current.filter(id => validIds.has(id)));
    } else {
      setFilteredWorkplaces(allWorkplaces);
    }
  }, [allWorkplaces, form]);

  const openCreate = async () => {
    setSelected(null);
    form.resetFields();
    setFilteredWorkplaces(allWorkplaces);
    setDrawerOpen(true);
    try {
      const r = await api.get<{ next_number: string }>('/equipment/next-number', { params: { category: 'equipment' } });
      form.setFieldsValue({ inventory_number: r.data.next_number });
    } catch { /* пользователь введёт вручную */ }
  };

  const openEdit = (record: Equipment) => {
    setSelected(record);
    if (record.department_id) {
      setFilteredWorkplaces(allWorkplaces.filter(w => w.department_id === record.department_id));
    } else {
      setFilteredWorkplaces(allWorkplaces);
    }
    form.setFieldsValue({
      inventory_number: record.inventory_number,
      name: record.name,
      brand: record.brand,
      model: record.model,
      year: record.year,
      purchase_cost: record.purchase_cost,
      receipt_url: record.receipt_url,
      type_id: record.type_id,
      department_id: record.department_id,
      workplace_ids: record.workplaces.map(w => w.id),
      status: record.status ?? 'active',
      assigned_employee_id: record.assigned_employee_id,
    });
    setDrawerOpen(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const payload = { ...values, category: 'equipment' };
      if (selected) {
        const updated = await updateEquipment(selected.id, payload);
        setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
        setSelected(updated);
        message.success('Обновлено');
      } else {
        const created = await createEquipment(payload);
        setItems(prev => [...prev, created]);
        setDrawerOpen(false);
        message.success('Создано');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка при сохранении');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteEquipment(id);
      setItems(prev => prev.filter(i => i.id !== id));
      message.success('Удалено');
    } catch {
      message.error('Ошибка при удалении');
    }
  };

  const columns = [
    { title: 'Инв. номер', dataIndex: 'inventory_number', key: 'inv', width: 130 },
    { title: 'Наименование', dataIndex: 'name', key: 'name' },
    {
      title: 'Марка / Модель', key: 'brand',
      render: (_: any, r: Equipment) => [r.brand, r.model].filter(Boolean).join(' / ') || '—',
    },
    {
      title: 'Тип', key: 'type', width: 130,
      render: (_: any, r: Equipment) =>
        r.type ? <Tag>{r.type.name}</Tag> : <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      title: 'Статус', key: 'status', width: 120,
      render: (_: any, r: Equipment) => (
        <Tag color={STATUS_COLORS[r.status ?? 'active']}>{STATUS_LABELS[r.status ?? 'active']}</Tag>
      ),
    },
    {
      title: 'Подразделение', key: 'dept', width: 160,
      render: (_: any, r: Equipment) => r.department?.name ?? <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      title: 'Рабочие места', key: 'workplaces',
      render: (_: any, r: Equipment) =>
        r.workplaces.length > 0
          ? r.workplaces.map(w => <Tag key={w.id}>{w.name}</Tag>)
          : <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      title: '', key: 'del', width: 50,
      render: (_: any, record: Equipment) => (
        <Popconfirm
          title="Удалить оборудование?"
          onConfirm={() => handleDelete(record.id)}
          okText="Да"
          cancelText="Нет"
        >
          <Button size="small" danger onClick={e => e.stopPropagation()}>✕</Button>
        </Popconfirm>
      ),
    },
  ];

  // Форма данных (переиспользуется в Drawer создания и вкладке "Данные")
  const dataForm = (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <Row gutter={12}>
        <Col span={10}>
          <Form.Item name="inventory_number" label="Инвентарный номер" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={14}>
          <Form.Item name="name" label="Наименование" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}><Form.Item name="brand" label="Марка"><Input /></Form.Item></Col>
        <Col span={12}><Form.Item name="model" label="Модель"><Input /></Form.Item></Col>
      </Row>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="status" label="Статус" initialValue="active">
            <Select options={Object.entries(STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }))} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="year" label="Год выпуска">
            <InputNumber style={{ width: '100%' }} min={1900} max={2100} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="purchase_cost" label="Стоимость (₽)">
            <InputNumber style={{ width: '100%' }} min={0} precision={2} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="type_id" label="Тип">
            <Select allowClear placeholder="Не указан"
              options={types.map(t => ({ value: t.id, label: t.name }))} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="receipt_url" label="Ссылка на чек">
            <Input placeholder="https://..." />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="department_id" label="Подразделение">
        <Select allowClear placeholder="Не указано"
          options={departments.map(d => ({ value: d.id, label: d.name }))}
          onChange={handleFormDeptChange} />
      </Form.Item>
      <Form.Item name="workplace_ids" label="Рабочие места">
        <Select mode="multiple" allowClear placeholder="Не привязано"
          options={filteredWorkplaces.map(w => ({ value: w.id, label: w.name }))} />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit">{selected ? 'Сохранить' : 'Создать'}</Button>
      </Form.Item>
    </Form>
  );

  const tabItems = selected ? [
    { key: 'data', label: 'Данные', children: dataForm },
    { key: 'maintenance', label: 'Журнал обслуживания', children: <MaintenanceLog equipmentId={selected.id} /> },
  ] : [];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}>Оборудование</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Добавить</Button>
      </Space>

      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col flex="200px">
          <Select allowClear placeholder="Тип" style={{ width: '100%' }}
            options={types.map(t => ({ value: t.id, label: t.name }))}
            onChange={setFilterType} />
        </Col>
        <Col flex="200px">
          <Select allowClear placeholder="Подразделение" style={{ width: '100%' }}
            options={departments.map(d => ({ value: d.id, label: d.name }))}
            onChange={setFilterDept} />
        </Col>
        <Col flex="auto">
          <Input.Search placeholder="Поиск по номеру, названию, марке..."
            allowClear onSearch={setFilterSearch} />
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20 }}
        onRow={record => ({ onClick: () => openEdit(record), style: { cursor: 'pointer' } })}
      />

      {/* Drawer создания (без вкладок) */}
      {!selected && (
        <Drawer
          title="Новое оборудование"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={560}
        >
          {dataForm}
        </Drawer>
      )}

      {/* Drawer редактирования с вкладками */}
      {selected && (
        <Drawer
          title={`${selected.name} (${selected.inventory_number})`}
          open={drawerOpen}
          onClose={() => { setDrawerOpen(false); setSelected(null); }}
          width={600}
        >
          <Tabs items={tabItems} />
        </Drawer>
      )}
    </div>
  );
};

export default EquipmentPage;
